// A table formatted as PowerPoint formats one (SB, Features 4): cells merged and split, each cell's own borders (which
// sides, a colour and a width), and a table style drawn from the deck's theme colours, with PowerPoint's style options
// (header row and banded rows, S7's; first column, here). Fields of their own on the table and its cells
// (shared/slideTable.ts), only where used: a table without them is drawn as before.

import { cleanColor, type SlideColor } from "./slideDesign";
import { MAX_TABLE_COLS, MAX_TABLE_ROWS, cleanTable, type SlideTable, type TableCell, type TableRow } from "./slideTable";
import type { DocNode } from "./docblocks";

/** One side of a cell: a line in a colour and a width (the slide's pixels), or none (null). */
export interface Edge {
  color: SlideColor;
  w: number;
  dash?: "dash" | "dot";
}

export interface CellEdges {
  t?: Edge | null;
  r?: Edge | null;
  b?: Edge | null;
  l?: Edge | null;
}

export interface CellFormat {
  /** the cell's own borders, side by side over the table's style (null: no line on that side) */
  bd?: CellEdges;
}

/** Table styles, as PowerPoint's Table Design offers them, each drawn in the deck's colours (the box's fill its colour). */
export const TABLE_STYLES = ["classic", "light", "medium", "grid", "dark", "minimal"] as const;
export type TableStyle = (typeof TABLE_STYLES)[number];
export const TABLE_STYLE_NAMES: Record<TableStyle, string> = { classic: "Classic", light: "Light", medium: "Medium", grid: "Grid", dark: "Dark", minimal: "Minimal" };

export interface TableFormat {
  /** its style; Classic (S7's look) when left out */
  style?: Exclude<TableStyle, "classic">;
  /** the first column stands out, as the header row does */
  first?: true;
}

/** The widest a cell's border is. */
export const MAX_EDGE_W = 16;

export function cleanEdge(raw: unknown): Edge | null | undefined {
  if (raw === null || raw === "none") return null;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const color = cleanColor(r.color);
  const w = typeof r.w === "number" && Number.isFinite(r.w) ? Math.round(Math.max(0.25, Math.min(MAX_EDGE_W, r.w)) * 100) / 100 : undefined;
  if (!color || w === undefined) return undefined;
  return { color, w, ...(r.dash === "dash" || r.dash === "dot" ? { dash: r.dash } : {}) };
}

const SIDES = ["t", "r", "b", "l"] as const;
type Side = (typeof SIDES)[number];

export function cleanCellFormat(r: Record<string, unknown>): CellFormat {
  const raw = r.bd;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const bd: CellEdges = {};
  for (const k of SIDES) {
    if (!Object.hasOwn(raw, k)) continue;
    const e = cleanEdge((raw as Record<string, unknown>)[k]);
    if (e !== undefined) bd[k] = e;
  }
  return Object.keys(bd).length ? { bd } : {};
}

export function cleanTableFormat(r: Record<string, unknown>): TableFormat {
  const out: TableFormat = {};
  if (typeof r.style === "string" && r.style !== "classic" && (TABLE_STYLES as readonly string[]).includes(r.style)) out.style = r.style as TableFormat["style"];
  if (r.first === true) out.first = true;
  return out;
}

// ---------------------------------------------------------------------------
// merging and splitting

/** The cell covering a place in the grid: its row and column (itself, or the merged cell over it). */
export function anchorAt(t: SlideTable, r: number, c: number): { r: number; c: number } | null {
  for (let i = r; i >= 0; i--)
    for (let j = c; j >= 0; j--) {
      const cell = t.rows[i]?.cells[j];
      if (!cell || cell.m) continue;
      if (i + (cell.rs ?? 1) > r && j + (cell.cs ?? 1) > c) return { r: i, c: j };
    }
  return null;
}

/** A rectangle of cells grown until no merged cell is cut by its edge: what Merge takes, and a selection really covers. */
export function wholeRange(t: SlideTable, r0: number, c0: number, r1: number, c1: number): [number, number, number, number] {
  let [ra, ca, rb, cb] = [Math.min(r0, r1), Math.min(c0, c1), Math.max(r0, r1), Math.max(c0, c1)];
  for (let grew = true; grew; ) {
    grew = false;
    for (let i = ra; i <= rb; i++)
      for (let j = ca; j <= cb; j++) {
        const a = anchorAt(t, i, j);
        if (!a) continue;
        const cell = t.rows[a.r].cells[a.c];
        const er = a.r + (cell.rs ?? 1) - 1;
        const ec = a.c + (cell.cs ?? 1) - 1;
        if (a.r < ra || a.c < ca || er > rb || ec > cb) {
          ra = Math.min(ra, a.r);
          ca = Math.min(ca, a.c);
          rb = Math.max(rb, er);
          cb = Math.max(cb, ec);
          grew = true;
        }
      }
  }
  return [ra, ca, rb, cb];
}

const hasWords = (c: TableCell) => (c.text?.content ?? []).some((n) => JSON.stringify(n).includes('"text":'));

/**
 * The cells of a rectangle merged into one (PowerPoint's Merge Cells): the top left cell spans them, keeping its look,
 * and holds every merged cell's words one after the other, in reading order.
 */
export function mergeCells(t: SlideTable, r0: number, c0: number, r1: number, c1: number): SlideTable {
  const [ra, ca, rb, cb] = wholeRange(t, r0, c0, r1, c1);
  if (ra === rb && ca === cb) return t;
  const words: DocNode[] = [];
  for (let i = ra; i <= rb; i++) for (let j = ca; j <= cb; j++) {
    const c = t.rows[i].cells[j];
    if (!c.m && hasWords(c)) words.push(...(c.text?.content ?? []));
  }
  const rows: TableRow[] = t.rows.map((row, i) => ({
    ...row,
    cells: row.cells.map((c, j) => {
      if (i < ra || i > rb || j < ca || j > cb) return c;
      if (i === ra && j === ca) {
        const { cs: _cs, rs: _rs, text: _t, ...look } = c;
        return { ...look, ...(words.length ? { text: { type: "doc" as const, content: words } } : {}), ...(cb > ca ? { cs: cb - ca + 1 } : {}), ...(rb > ra ? { rs: rb - ra + 1 } : {}) };
      }
      return { m: true as const };
    }),
  }));
  return cleanTable({ ...t, rows }) ?? t;
}

/** A merged cell split back into the cells it covered, each in its look, its words staying in the first. */
export function unmergeCell(t: SlideTable, r: number, c: number): SlideTable {
  const a = anchorAt(t, r, c);
  if (!a) return t;
  const cell = t.rows[a.r].cells[a.c];
  const rs = cell.rs ?? 1;
  const cs = cell.cs ?? 1;
  if (rs === 1 && cs === 1) return t;
  const { text: _t, cs: _cs, rs: _rs, bd, ...look } = cell;
  const rows: TableRow[] = t.rows.map((row, i) => ({
    ...row,
    cells: row.cells.map((x, j) => {
      if (i < a.r || i >= a.r + rs || j < a.c || j >= a.c + cs) return x;
      // each new cell keeps the merged cell's outer borders where it lies on them
      const own: CellEdges = {};
      if (bd) {
        if (i === a.r && bd.t !== undefined) own.t = bd.t;
        if (i === a.r + rs - 1 && bd.b !== undefined) own.b = bd.b;
        if (j === a.c && bd.l !== undefined) own.l = bd.l;
        if (j === a.c + cs - 1 && bd.r !== undefined) own.r = bd.r;
      }
      const base: TableCell = { ...look, ...(Object.keys(own).length ? { bd: own } : {}) };
      return i === a.r && j === a.c ? { ...base, ...(cell.text ? { text: cell.text } : {}) } : base;
    }),
  }));
  return cleanTable({ ...t, rows }) ?? t;
}

/**
 * A cell split in two (PowerPoint's Split Cells, two columns or two rows): a new column (or row) goes beside it, half its
 * width (or height), and every other cell in that column (or row) grows over the new one, so only this cell splits.
 * A merged cell is split back into the cells it covered instead.
 */
export function splitCell(t: SlideTable, r: number, c: number, into: "cols" | "rows"): SlideTable {
  const a = anchorAt(t, r, c);
  if (!a) return t;
  const cell = t.rows[a.r].cells[a.c];
  if ((cell.rs ?? 1) > 1 || (cell.cs ?? 1) > 1) return unmergeCell(t, r, c);
  if (into === "cols") {
    if (t.cols.length >= MAX_TABLE_COLS) return t;
    const cols = [...t.cols];
    const half = cols[a.c] / 2;
    cols.splice(a.c, 1, half, half);
    const rows: TableRow[] = t.rows.map((row, i) => {
      const cells: TableCell[] = [];
      row.cells.forEach((x, j) => {
        if (j !== a.c) {
          // a merge reaching over the split column grows by one
          const span = x.m ? x : (x.cs ?? 1) > 1 && j < a.c && j + (x.cs ?? 1) > a.c ? { ...x, cs: (x.cs ?? 1) + 1 } : x;
          cells.push(span);
          return;
        }
        if (i === a.r) {
          const { text: _t, bd, ...look } = x;
          cells.push({ ...x, ...(bd ? { bd: { ...bd, r: undefined } } : {}) }, { ...look, ...(bd ? { bd: { ...bd, l: undefined } } : {}) });
        } else if (x.m) cells.push(x, { m: true });
        else cells.push({ ...x, cs: (x.cs ?? 1) + 1 }, { m: true });
      });
      return { ...row, cells };
    });
    return cleanTable({ ...t, cols, rows: rows.map((row) => ({ ...row, cells: row.cells.map(dropUndefinedEdges) })) }) ?? t;
  }
  if (t.rows.length >= MAX_TABLE_ROWS) return t;
  const rowsOut: TableRow[] = [];
  t.rows.forEach((row, i) => {
    if (i !== a.r) {
      rowsOut.push({ ...row, cells: row.cells.map((x) => (!x.m && i < a.r && i + (x.rs ?? 1) > a.r ? { ...x, rs: (x.rs ?? 1) + 1 } : x)) });
      return;
    }
    const top: TableCell[] = [];
    const below: TableCell[] = [];
    row.cells.forEach((x, j) => {
      if (j === a.c) {
        const { text: _t, bd, ...look } = x;
        top.push(dropUndefinedEdges({ ...x, ...(bd ? { bd: { ...bd, b: undefined } } : {}) }));
        below.push(dropUndefinedEdges({ ...look, ...(bd ? { bd: { ...bd, t: undefined } } : {}) }));
      } else if (x.m) {
        top.push(x);
        below.push({ m: true });
      } else {
        top.push({ ...x, rs: (x.rs ?? 1) + 1 });
        below.push({ m: true });
      }
    });
    rowsOut.push({ h: row.h / 2, cells: top }, { h: row.h / 2, cells: below });
  });
  return cleanTable({ ...t, rows: rowsOut }) ?? t;
}

function dropUndefinedEdges(c: TableCell): TableCell {
  if (!c.bd) return c;
  const bd = Object.fromEntries(Object.entries(c.bd).filter(([, v]) => v !== undefined)) as CellEdges;
  if (Object.keys(bd).length) return { ...c, bd };
  const { bd: _b, ...rest } = c;
  return rest;
}

/** Every row as tall as the others (PowerPoint's Distribute Rows); a row with more words still grows. */
export const evenRows = (t: SlideTable): SlideTable => cleanTable({ ...t, rows: t.rows.map((r) => ({ ...r, h: 1 })) }) ?? t;

// ---------------------------------------------------------------------------
// borders on the cells picked, as PowerPoint's Borders menu sets them

export const BORDER_SETS = ["all", "outside", "inside", "top", "bottom", "left", "right", "insideH", "insideV", "none"] as const;
export type BorderSet = (typeof BORDER_SETS)[number];
export const BORDER_NAMES: Record<BorderSet, string> = { all: "All borders", outside: "Outside borders", inside: "Inside borders", top: "Top border", bottom: "Bottom border", left: "Left border", right: "Right border", insideH: "Inside horizontal", insideV: "Inside vertical", none: "No border" };

const OPP: Record<Side, Side> = { t: "b", b: "t", l: "r", r: "l" };

/**
 * The cells of a rectangle given borders (an edge, or none): which sides by `which`, as PowerPoint's Borders menu does.
 * The cell across each side changed is given the same line on its own side, so both cells agree.
 */
export function setBorders(t: SlideTable, r0: number, c0: number, r1: number, c1: number, which: BorderSet, edge: Edge | null): SlideTable {
  const [ra, ca, rb, cb] = wholeRange(t, r0, c0, r1, c1);
  const rows = t.rows.map((row) => ({ ...row, cells: row.cells.map((c) => ({ ...c, ...(c.bd ? { bd: { ...c.bd } } : {}) })) }));
  const put = (i: number, j: number, side: Side, e: Edge | null) => {
    const a = anchorAt({ ...t, rows }, i, j);
    if (!a) return;
    const cell = rows[a.r].cells[a.c];
    cell.bd = { ...(cell.bd ?? {}), [side]: e };
  };
  const wants = (side: Side, outer: boolean): boolean => {
    switch (which) {
      case "all":
      case "none":
        return true;
      case "outside":
        return outer;
      case "inside":
        return !outer;
      case "insideH":
        return !outer && (side === "t" || side === "b");
      case "insideV":
        return !outer && (side === "l" || side === "r");
      case "top":
        return outer && side === "t";
      case "bottom":
        return outer && side === "b";
      case "left":
        return outer && side === "l";
      case "right":
        return outer && side === "r";
    }
  };
  const e = which === "none" ? null : edge;
  for (let i = ra; i <= rb; i++)
    for (let j = ca; j <= cb; j++) {
      const a = anchorAt(t, i, j);
      if (!a) continue;
      const cell = t.rows[a.r].cells[a.c];
      const er = a.r + (cell.rs ?? 1) - 1;
      const ec = a.c + (cell.cs ?? 1) - 1;
      const sides: [Side, boolean, boolean][] = [
        // side, a real edge of the cell here, on the rectangle's outside
        ["t", i === a.r, i === ra],
        ["b", i === er, i === rb],
        ["l", j === a.c, j === ca],
        ["r", j === ec, j === cb],
      ];
      for (const [side, real, outer] of sides) {
        if (!real || !wants(side, outer)) continue;
        put(i, j, side, e);
        // the cell across that side, when there is one
        const ni = side === "t" ? i - 1 : side === "b" ? i + 1 : i;
        const nj = side === "l" ? j - 1 : side === "r" ? j + 1 : j;
        if (ni >= 0 && nj >= 0 && ni < t.rows.length && nj < t.cols.length) put(ni, nj, OPP[side], e);
      }
    }
  return cleanTable({ ...t, rows }) ?? t;
}

/** The cells of a rectangle with their own borders taken off (back to the table's style). */
export function clearBorders(t: SlideTable, r0: number, c0: number, r1: number, c1: number): SlideTable {
  const [ra, ca, rb, cb] = wholeRange(t, r0, c0, r1, c1);
  const rows = t.rows.map((row, i) => ({ ...row, cells: row.cells.map((c, j) => (i >= ra && i <= rb && j >= ca && j <= cb && c.bd ? (({ bd: _b, ...rest }) => rest)(c) : c)) }));
  return cleanTable({ ...t, rows }) ?? t;
}

/** The side of a cell, after its own and its neighbour's: undefined when neither says (the style draws it). */
export function ownEdge(t: SlideTable, r: number, c: number, side: Side): Edge | null | undefined {
  const cell = t.rows[r]?.cells[c];
  if (!cell || cell.m) return undefined;
  const mine = cell.bd?.[side];
  if (mine !== undefined) return mine;
  // the cell across: its own opposite side, for the stretch of this side it shares
  const rs = cell.rs ?? 1;
  const cs = cell.cs ?? 1;
  const probe = side === "t" ? [r - 1, c] : side === "b" ? [r + rs, c] : side === "l" ? [r, c - 1] : [r, c + cs];
  const a = anchorAt(t, probe[0], probe[1]);
  if (!a) return undefined;
  return t.rows[a.r].cells[a.c].bd?.[OPP[side]];
}
